import { Box } from '@mui/material';
import { Formik, Form } from 'formik';
import { useSelector } from 'react-redux';

import FormSkeleton from './formSkeleton';
import UploadCVButton from './uploadCVButton';
import FormField from 'components/common/formikForm/formField';
import { FormBusyProvider } from 'components/common/formikForm/formBusyContext';
import FormGridLayout from 'components/common/formikForm/formGridLayout';
import { StepNavigation } from 'components/v2/StepNavigation';

import { visaPreferenceOptions } from 'constant';
import { useSavePersonalInfo, useSavePurpose, useSaveCitizenship } from 'hooks';
import { useCountries, useCitizenship, usePersonalInfo, usePurpose } from 'hooks';
import { RootState } from 'redux/store';
import type { PersonInfoForm } from 'types';
import { generateOptionsFromString } from 'utils/dataFormatter';
import { isProfileOnlySpouseSelected } from 'utils/selectedAccount';
import { getPersonalInfoValidationSchema } from 'utils/validations/personalInfoSchema';

interface PersonalInfoStepProps {
    isLastStep: boolean;
    onNext: (data: Record<string, unknown>) => void;
    onBack: () => void;
    onPurposeChange?: (purpose: string) => void;
}

const PersonalInfoStep = ({
    onNext,
    onBack,
    onPurposeChange,
    isLastStep,
}: PersonalInfoStepProps) => {
    const { id: currentUserId, spouse } = useSelector((state: RootState) => state.user.entity);
    const selectedUserId = useSelector((state: RootState) => state.selectedAccount.selectedUserId);
    const isEmailOptional = isProfileOnlySpouseSelected(currentUserId, spouse, selectedUserId);
    const { data: apiData, isLoading } = usePersonalInfo();
    const { data: purpose, isLoading: purposeLoading } = usePurpose();
    const { data: citizenship, isLoading: citizenshipLoading } = useCitizenship();
    const { data: countries, isLoading: countriesLoading } = useCountries();

    const savePersonalInfoMutation = useSavePersonalInfo();
    const savePurposeMutation = useSavePurpose();
    const saveCitizenshipMutation = useSaveCitizenship();

    const countryOptions = generateOptionsFromString(countries ?? []);

    const initialValues: Partial<PersonInfoForm> = {
        email: apiData?.email || '',
        firstname: apiData?.firstname || '',
        lastname: apiData?.lastname || '',
        middlename: apiData?.middlename || '',
        phone_number: apiData?.phone_number || '',
        purpose: purpose?.purpose || '',
        citizenship: citizenship?.map(({ country }) => country) ?? [],
    };

    const handleSubmit = async (values: Partial<PersonInfoForm>) => {
        try {
            const personalInfoValues = { ...values };
            if (isEmailOptional && !values.email) {
                delete personalInfoValues.email;
            }
            // Save all three pieces of data in parallel
            const response = await Promise.all([
                savePersonalInfoMutation.mutateAsync(personalInfoValues as PersonInfoForm),
                savePurposeMutation.mutateAsync(values.purpose || ''),
                saveCitizenshipMutation.mutateAsync(values.citizenship ?? []),
            ]);
            onNext({
                personalInfo: response[0],
                purpose: response[1],
                citizenship: response[2],
            });
        } catch {
            // Error alert is handled by the mutation hook onError
        }
    };

    if (isLoading || countriesLoading || purposeLoading || citizenshipLoading) {
        return <FormSkeleton fields={6} />;
    }

    const isSubmittingForm =
        savePersonalInfoMutation.isPending ||
        savePurposeMutation.isPending ||
        saveCitizenshipMutation.isPending;

    return (
        <Box>
            <Formik
                initialValues={initialValues}
                validationSchema={getPersonalInfoValidationSchema(!isEmailOptional)}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({ isValid, isSubmitting, setFieldValue }) => {
                    const isLoading = isSubmitting || isSubmittingForm;

                    return (
                        <FormBusyProvider busy={isLoading}>
                            <Form>
                                <Box sx={{ mb: 3 }}>
                                    <UploadCVButton />
                                </Box>

                                <FormGridLayout>
                                    <FormField
                                        required
                                        type="autosuggest"
                                        name="purpose"
                                        label="selectVisaPreference"
                                        options={visaPreferenceOptions}
                                        disabled={isLoading}
                                        onChange={(value) => {
                                            setFieldValue('purpose', value);
                                            if (onPurposeChange) {
                                                onPurposeChange(value as string);
                                            }
                                        }}
                                    />

                                    <FormField
                                        required
                                        type="autosuggest"
                                        multiple
                                        name="citizenship"
                                        label="citizenshipSelection"
                                        options={countryOptions}
                                        disabled={isLoading}
                                    />

                                    <FormField
                                        required
                                        name="firstname"
                                        label="firstName"
                                        disabled={isLoading}
                                    />

                                    <FormField
                                        required
                                        name="lastname"
                                        label="lastName"
                                        disabled={isLoading}
                                    />

                                    <FormField
                                        required={!isEmailOptional}
                                        name="email"
                                        label="email"
                                        type="email"
                                        disabled={isLoading}
                                    />

                                    <FormField
                                        required
                                        type="phone"
                                        name="phone_number"
                                        label="phone"
                                        disabled={isLoading}
                                    />
                                </FormGridLayout>

                                <StepNavigation
                                    isFirstStep
                                    isLastStep={isLastStep}
                                    onBack={onBack}
                                    isSubmitting={isLoading}
                                    disableNext={!isValid}
                                />
                            </Form>
                        </FormBusyProvider>
                    );
                }}
            </Formik>
        </Box>
    );
};

export default PersonalInfoStep;
